/** @jsxImportSource @compiled/react */
import { useCallback, useEffect, useState } from "react";

import { useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { ConfluenceIcon } from "@atlaskit/logo-next";

import { experiments, useExperiment } from "@/utils/experiment";
import { getLocalStorage, setLocalStorage } from "@/utils/localstorage";

import { SmartBanner } from "./smart-banner";

const STORAGE_KEY = "SmartBanner_dismiss-confluence";
const DISMISS_EXPIRY = 24 * 60 * 60;

const IOS_APP_STORE_URL =
    "https://apps.apple.com/app/confluence-cloud/id1006971684";
const APPLE_APP_ID = "1006971684";

// Android Intent URL with built-in Play Store fallback
const ANDROID_PLAY_STORE_URL =
    "https://play.google.com/store/apps/details?id=com.atlassian.android.confluence.core";
const ANDROID_INTENT_URL = `intent://home#Intent;scheme=confluence;package=com.atlassian.android.confluence.core;S.browser_fallback_url=${encodeURIComponent(
    ANDROID_PLAY_STORE_URL
)};end`;

type MobileDeviceInfo = {
    isMobile: boolean;
    isIOS: boolean;
    isAndroid: boolean;
    isSafari: boolean;
};

const useMobileDeviceInfo = (): MobileDeviceInfo => {
    const [deviceInfo, setDeviceInfo] = useState<MobileDeviceInfo>({
        isMobile: false,
        isIOS: false,
        isAndroid: false,
        isSafari: false
    });

    useEffect(() => {
        if (typeof window !== "undefined" && window.navigator) {
            const userAgent = window.navigator.userAgent;
            const isIOS = /iPhone|iPad|iPod/i.test(userAgent);
            const isAndroid = /Android/i.test(userAgent);
            const isSafari =
                /^((?!chrome|android|crios|fxios).)*safari/i.test(userAgent) &&
                isIOS;
            setDeviceInfo({
                isMobile: isIOS || isAndroid,
                isIOS,
                isAndroid,
                isSafari
            });
        }
    }, []);

    return deviceInfo;
};

export interface ConfluenceSmartBannerProps {
    className?: string;
}

/**
 * ConfluenceSmartBanner - iOS/Android smart app banner for Confluence
 *
 * This component:
 * - Only renders on iOS and Android devices
 * - Only renders on /software/confluence and /software/confluence/* paths
 * - Is controlled by the gwp9508ConfluenceBannerIosNativeApp experiment
 * - Persists dismiss state in localStorage for 24 hours
 * - Attempts to open the Confluence app via App Store URL, Safari uses native smart banner
 */
export const ConfluenceSmartBanner = ({
    className
}: ConfluenceSmartBannerProps) => {
    const { isMobile, isAndroid, isSafari } = useMobileDeviceInfo();
    const [isDismissed, setIsDismissed] = useState(true);
    const intl = useIntl();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const experimentStatus = useExperiment(
        experiments.gwp9508ConfluenceBannerIosNativeApp
    );

    const isExperimentEnabled =
        experimentStatus.type === "enrolled" && experimentStatus.value === true;

    useEffect(() => {
        if (isExperimentEnabled && isSafari) {
            const meta = document.createElement("meta");
            meta.name = "apple-itunes-app";
            meta.content = `app-id=${APPLE_APP_ID}, ct=wac_smart_banner`;
            document.head.appendChild(meta);

            return () => {
                if (meta.parentNode) {
                    meta.parentNode.removeChild(meta);
                }
            };
        }
    }, [isExperimentEnabled, isSafari]);

    useEffect(() => {
        const dismissValue = getLocalStorage(STORAGE_KEY, true);
        if (dismissValue !== "1") {
            setIsDismissed(false);
        }
    }, []);

    const handleClose = () => {
        setIsDismissed(true);
        setLocalStorage(STORAGE_KEY, "1", DISMISS_EXPIRY);
        createAnalyticsEvent({
            action: "clicked",
            actionSubject: "button",
            actionSubjectId: "smartBannerClose",
            eventType: "ui",
            attributes: {
                productKey: "confluence",
                eventContainer: "smartBanner"
            }
        }).fire();
    };

    const getDeepLinkUrl = useCallback(() => {
        if (isAndroid) {
            return ANDROID_INTENT_URL;
        }
        return IOS_APP_STORE_URL;
    }, [isAndroid]);

    if (!isMobile || !isExperimentEnabled || isDismissed || isSafari) {
        return null;
    }

    return (
        <SmartBanner
            appIcon={
                <ConfluenceIcon
                    appearance="brand"
                    size="medium"
                    shouldUseNewLogoDesign
                />
            }
            title={intl.formatMessage({
                id: "ConfluenceSmartBanner.title",
                defaultMessage: "Get the Confluence app"
            })}
            subtitle={intl.formatMessage({
                id: "ConfluenceSmartBanner.subtitle",
                defaultMessage: "Collaborate on the go"
            })}
            buttonText={intl.formatMessage({
                id: "ConfluenceSmartBanner.buttonText",
                defaultMessage: "OPEN"
            })}
            buttonHref={getDeepLinkUrl()}
            buttonAriaLabel={intl.formatMessage({
                id: "ConfluenceSmartBanner.buttonAriaLabel",
                defaultMessage: "Open Confluence app"
            })}
            onClose={handleClose}
            className={className}
            dataEventProps={{
                "data-product-key": "confluence",
                "data-event-container": "smartBanner",
                "data-label": "OPEN",
                "data-label-english": "OPEN"
            }}
        />
    );
};

export default ConfluenceSmartBanner;
